import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { type Timeframe } from './useStudentGenerateMessageMutation';
import { timeframeToApiParams } from './studentDataParams';

const log = createLogger('useStudentBehaviorReportQuery');

export const STUDENT_BEHAVIOR_REPORT_QUERY_KEY = 'studentBehaviorReport';

export type BehaviorSeries = {
  key: string;
  label: string;
  data: (number | null)[];
};

/** Chart-ready shape consumed by `BehaviorChartView`. `ticks` are raw ISO dates;
 *  the chart formats and thins them for display. */
export type BehaviorReportChartData = {
  ticks: string[];
  series: BehaviorSeries[];
};

type BehaviorReportApiResponse = {
  ticks: string[];
  series: { key: string; label: string; data: (number | null)[] }[];
  interval: string;
};

/** "student-abc123_praises" → "praises" */
function normalizeKey(key: string): string {
  const idx = key.lastIndexOf('_');
  return idx >= 0 ? key.slice(idx + 1) : key;
}

/** "Aaron (Praises)" → "Praises" */
function simplifyLabel(label: string): string {
  const match = label.match(/\(([^)]+)\)$/);
  return match?.[1] ?? label;
}

export function useStudentBehaviorReportQuery({
  studentId,
  periodId,
  periodCreatedAt,
  timeframe,
}: {
  studentId: string;
  periodId: string;
  periodCreatedAt: string;
  timeframe: Timeframe;
}) {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, status, isFetching } = useQuery<BehaviorReportChartData | null>({
    queryKey: [STUDENT_BEHAVIOR_REPORT_QUERY_KEY, studentId, periodId, timeframe, periodCreatedAt],
    queryFn: async () => {
      log.debug('Fetching student behavior report', { studentId, periodId, timeframe });

      const tz = locale?.timeZone ?? '';
      const dateParams = timeframeToApiParams(timeframe, periodCreatedAt);

      const params = new URLSearchParams({
        tz,
        studentIds: studentId,
        periodIds: periodId,
        indication: 'both',
        overlay: 'absence',
        interval: 'day',
      });
      if ('range' in dateParams && dateParams.range !== undefined) {
        params.set('range', dateParams.range);
      } else if ('start' in dateParams && dateParams.start !== undefined) {
        params.set('start', dateParams.start);
        params.set('end', dateParams.end!);
      }

      const result = await httpClient.get<BehaviorReportApiResponse>(`/report/getBehaviorReport?${params}`);

      const chartData: BehaviorReportChartData = {
        ticks: result.ticks,
        series: result.series.map((s) => {
          const key = normalizeKey(s.key);
          const data = key === 'absent' ? s.data.map((v) => (v === 0 ? null : v)) : s.data;
          return { key, label: simplifyLabel(s.label), data };
        }),
      };

      log.info('Fetched student behavior report successfully', { studentId, tickCount: result.ticks.length });
      return chartData;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch student behavior report', failureCount, error, log }),
    enabled: !!studentId && !!periodId,
    refetchOnWindowFocus: false,
  });

  return { data, status, isFetching };
}
